import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { acceptInvitation, fetchInvitation } from "../../lib/api"
import { extractErrorMessage } from "../../lib/api/errors"
import { queryKeys } from "../../lib/query-keys"
import { useProduct, useTenant } from "../../lib/tenant"
import { InviteView } from "./-InviteView"
import type { TInviteState } from "./-InviteView/interfaces"

export const Route = createFileRoute("/invite/$id")({
  component: InvitePage,
})

function InvitePage(): React.JSX.Element {
  const { id } = Route.useParams()
  const branding = useTenant().branding
  const product = useProduct()
  const { i18n } = useLingui()
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | undefined>(undefined)
  const [forcedState, setForcedState] = useState<TInviteState | undefined>(undefined)

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.invitation(id),
    queryFn: () => fetchInvitation(id),
    retry: false,
  })

  const derivedState: TInviteState = (() => {
    if (forcedState) return forcedState
    if (isLoading) return "loading"
    if (!data?.found) return "invalid"
    if (data.expired || data.status !== "pending") return "invalid"
    if (data.userExists) return "needs-login"
    return "form"
  })()

  const organizationName = data?.found ? data.organizationName : undefined
  const email = data?.found ? data.email : undefined

  const handleSubmit = async (name: string, password: string): Promise<void> => {
    setSubmitError(undefined)
    setSubmitting(true)
    try {
      const result = await acceptInvitation(id, { name, password })
      if (result.success) {
        setForcedState("success")
        // Session cookie is set; land on the tenant dashboard (same origin).
        window.location.href = "/"
        return
      }
      if (result.status === 409 || result.needsLogin) {
        setForcedState("needs-login")
        return
      }
      setSubmitError(result.error ?? i18n._(msg`Couldn't accept the invitation. It may have expired.`))
    } catch (err) {
      setSubmitError(extractErrorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <InviteView
      logo={branding?.logo}
      appName={branding?.name}
      product={product}
      loginBackground={branding?.loginBackground}
      state={derivedState}
      organizationName={organizationName}
      email={email}
      error={submitError}
      loading={submitting}
      loginHref={`/login?invite=${id}`}
      onSubmit={handleSubmit}
    />
  )
}
